// Bank VAULT storage overlay — an upgradable second inventory kept at the bank. Left: the vault
// (capacity grows per purchased tier); right: your bag. Click an item in your bag to STORE it, or
// in the vault to RETRIEVE it. The "Expand vault" button buys the next tier with bank money. The
// server is authoritative; this just mirrors storageStore + inventoryStore and sends intents.

import { STORAGE_COLS, INVENTORY_COLS, type InventorySlot } from "@ghostland/shared";
import { storageStore } from "../items/storageStore.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { statsStore } from "../items/statsStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { injectBillStyle, bankIcon } from "./pixelBills.js";

export interface StorageActions {
  request: () => void;
  /** Move one unit bag → vault; `to` = the drop-target vault slot (omitted on click = auto-place). */
  store: (slotIndex: number, to?: number) => void;
  /** Move one unit vault → bag; `to` = the drop-target bag slot (omitted on click = auto-place). */
  retrieve: (slotIndex: number, to?: number) => void;
  /** Drag-reorder INSIDE the vault (move/merge/swap — the bag's slot-to-slot semantics). */
  move: (from: number, to: number) => void;
  /** Drag-reorder INSIDE the bag grid (routes to the normal inventory MoveItem). */
  bagMove: (from: number, to: number) => void;
  upgrade: () => void;
}
let actions: StorageActions | null = null;
export function setStorageActions(a: StorageActions): void { actions = a; }

const STYLE_ID = "ghostland-storage-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-stor-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(0,0,0,.7); color: #e8e8e8;
}
#gl-stor-overlay, #gl-stor-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-stor-overlay.open { display: flex; }
#gl-stor-card { width: 520px; padding: 20px 18px 16px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-stor-card h1 { margin: 0 0 2px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-stor-card .hint { font-size: 7px; color: #7a7a7a; letter-spacing: 1px; margin-bottom: 14px; }
.gl-stor-cols { display: flex; gap: 18px; }
.gl-stor-col { flex: 1; }
.gl-stor-col h2 { margin: 0 0 8px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; }
.gl-stor-grid { display: grid; gap: 4px; }
.gl-stor-cell {
  width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a;
  display: flex; align-items: flex-end; justify-content: flex-end; position: relative; cursor: pointer;
}
.gl-stor-cell.empty { cursor: default; }
.gl-stor-cell:not(.empty):hover { border-color: #f4f4f4; }
.gl-stor-cell.drop-hint { border-color: var(--gl-accent, #b48cff); box-shadow: 0 0 0 2px rgba(180,140,255,.4) inset; }
.gl-stor-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-stor-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
#gl-stor-bar { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 12px; border-top: 1px solid #1c1c1c; }
#gl-stor-tier { font-size: 8px; color: #8a8a8a; letter-spacing: 1px; }
#gl-stor-bank { font-size: 9px; color: #d8d8d8; display: inline-flex; align-items: center; gap: 5px; }
.gl-stor-btn { background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; padding: 7px 12px; cursor: pointer; font-size: 8px; letter-spacing: 1px; image-rendering: pixelated; }
.gl-stor-btn:hover:not(:disabled) { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
.gl-stor-btn:disabled { opacity: .35; cursor: default; }
#gl-stor-close { margin-top: 10px; width: 100%; padding: 7px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
#gl-stor-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
`;

let overlay: HTMLDivElement | null = null;
let vaultGrid: HTMLDivElement | null = null;
let bagGrid: HTMLDivElement | null = null;
let vaultTitle: HTMLHeadingElement | null = null;
let tierEl: HTMLDivElement | null = null;
let bankEl: HTMLDivElement | null = null;
let upBtn: HTMLButtonElement | null = null;
let unsub: Array<() => void> = [];

/** Route a drop landing on `target` grid slot `to` from the drag payload "src:index". */
function routeDrop(payload: string, target: "vault" | "bag", to: number): void {
  const [src, idxStr] = payload.split(":");
  const from = Number(idxStr);
  if (!Number.isInteger(from)) return;
  if (src === target) {
    if (from === to) return;
    if (target === "vault") actions?.move(from, to);   // reorder inside the vault
    else actions?.bagMove(from, to);                    // reorder inside the bag
  } else if (target === "vault" && src === "bag") actions?.store(from, to);
  else if (target === "bag" && src === "vault") actions?.retrieve(from, to);
}

function cell(slot: InventorySlot | undefined, index: number, onClick: (() => void) | null, source: "vault" | "bag"): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "gl-stor-cell" + (slot ? "" : " empty");
  if (slot) {
    el.appendChild(itemThumb(slot.itemId));
    if (slot.quantity > 1) { const q = document.createElement("span"); q.className = "q"; q.textContent = String(slot.quantity); el.appendChild(q); }
    if (onClick) el.addEventListener("click", onClick);
    // drag a filled cell onto ANY cell: same grid = reorder, other grid = store/retrieve there
    el.draggable = true;
    el.addEventListener("dragstart", (e) => {
      e.dataTransfer?.setData("text/plain", `${source}:${slot.slotIndex}`);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    });
  }
  // Every cell (filled or empty) is a slot-to-slot drop target — matching the bag's behaviour.
  el.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; el.classList.add("drop-hint"); });
  el.addEventListener("dragleave", () => el.classList.remove("drop-hint"));
  el.addEventListener("drop", (e) => {
    e.preventDefault();
    el.classList.remove("drop-hint");
    routeDrop(e.dataTransfer?.getData("text/plain") ?? "", source, index);
  });
  return el;
}

function renderGrid(grid: HTMLDivElement, count: number, cols: number, slots: InventorySlot[], move: (i: number) => void, source: "vault" | "bag"): void {
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  const by = new Map(slots.map((s) => [s.slotIndex, s]));
  const cells: HTMLDivElement[] = [];
  for (let i = 0; i < count; i++) {
    const s = by.get(i);
    cells.push(cell(s, i, s ? () => move(i) : null, source));
  }
  grid.replaceChildren(...cells);
}

function refresh(): void {
  const st = storageStore.get();
  const bag = inventoryStore.get();
  if (vaultTitle) vaultTitle.textContent = `BANK VAULT  ${st.slots.length}/${st.capacity}`;
  if (vaultGrid) renderGrid(vaultGrid, st.capacity, STORAGE_COLS, st.slots, (i) => actions?.retrieve(i), "vault");
  if (bagGrid) renderGrid(bagGrid, 20, INVENTORY_COLS, bag, (i) => actions?.store(i), "bag");
  if (tierEl) tierEl.textContent = `TIER ${st.tier + 1}`;
  if (bankEl) bankEl.innerHTML = `${bankIcon()} <b>${statsStore.get().bankMoney.toLocaleString()}</b>`;
  if (upBtn) {
    if (st.upgradeCost === null) { upBtn.textContent = "VAULT MAXED"; upBtn.disabled = true; }
    else { upBtn.textContent = `EXPAND VAULT  $${st.upgradeCost.toLocaleString()}`; upBtn.disabled = false; }
  }
}

function ensure(): void {
  injectBillStyle();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-stor-overlay";
  overlay.innerHTML = `
    <div id="gl-stor-card">
      <h1>BANK STORAGE</h1>
      <div class="hint">click or drag items between your bag and the vault — money is unlimited in the bank</div>
      <div class="gl-stor-cols">
        <div class="gl-stor-col"><h2 id="gl-stor-vtitle">BANK VAULT</h2><div class="gl-stor-grid" id="gl-stor-vault"></div></div>
        <div class="gl-stor-col"><h2>YOUR BAG</h2><div class="gl-stor-grid" id="gl-stor-bag"></div></div>
      </div>
      <div id="gl-stor-bar">
        <div id="gl-stor-tier">TIER 1</div>
        <div id="gl-stor-bank">${bankIcon()} <b>0</b></div>
        <button class="gl-stor-btn" id="gl-stor-up">EXPAND VAULT</button>
      </div>
      <button id="gl-stor-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  vaultGrid = overlay.querySelector("#gl-stor-vault");
  bagGrid = overlay.querySelector("#gl-stor-bag");
  vaultTitle = overlay.querySelector("#gl-stor-vtitle");
  tierEl = overlay.querySelector("#gl-stor-tier");
  bankEl = overlay.querySelector("#gl-stor-bank");
  upBtn = overlay.querySelector("#gl-stor-up");
  upBtn!.addEventListener("click", () => actions?.upgrade());
  overlay.querySelector("#gl-stor-close")!.addEventListener("click", closeStorage);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeStorage(); });
}

export function openStorage(): void {
  ensure();
  overlay!.classList.add("open");
  unsub = [storageStore.subscribe(refresh), inventoryStore.subscribe(refresh), statsStore.subscribe(refresh)];
  actions?.request();
}

export function closeStorage(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}

export function isStorageOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
